import { generateId } from './measurements';
import { GRID_SIZE } from '../constants/grid';

/**
 * Convert feet to pixels
 * GRID_SIZE = 20 pixels = 6 inches
 * So 1 foot = 40 pixels
 */
const feetToPixels = (feet) => feet * GRID_SIZE * 2;

/**
 * Get room color based on room name
 */
const getRoomColor = (name) => {
  const n = name.toLowerCase();
  if (n.includes('garage') || n.includes('shop')) return 'rgba(150, 150, 150, 0.15)';
  if (n.includes('master bed') || n.includes('master suite')) return 'rgba(120, 150, 255, 0.18)';
  if (n.includes('bedroom') || n.includes('bed ')) return 'rgba(200, 150, 255, 0.15)';
  if (n.includes('master bath')) return 'rgba(100, 220, 255, 0.18)';
  if (n.includes('bath') || n.includes('powder')) return 'rgba(100, 255, 200, 0.15)';
  if (n.includes('kitchen')) return 'rgba(255, 200, 100, 0.15)';
  if (n.includes('living') || n.includes('great')) return 'rgba(100, 200, 255, 0.15)';
  if (n.includes('dining')) return 'rgba(255, 150, 150, 0.15)';
  if (n.includes('closet')) return 'rgba(200, 200, 200, 0.15)';
  if (n.includes('laundry') || n.includes('utility') || n.includes('mudroom')) return 'rgba(150, 220, 255, 0.15)';
  if (n.includes('porch') || n.includes('patio') || n.includes('deck')) return 'rgba(139, 119, 101, 0.2)';
  if (n.includes('office')) return 'rgba(150, 200, 150, 0.15)';
  if (n.includes('pantry')) return 'rgba(220, 180, 140, 0.15)';
  if (n.includes('hall') || n.includes('entry')) return 'rgba(180, 180, 220, 0.12)';
  return 'rgba(200, 200, 200, 0.12)';
};

/**
 * Determine wall type based on room names it borders
 */
const getWallType = (room1Name, room2Name, isExterior) => {
  if (isExterior) return 'exterior';

  const n1 = room1Name?.toLowerCase() || '';
  const n2 = room2Name?.toLowerCase() || '';

  // Garage walls are usually heavier
  if (n1.includes('garage') || n1.includes('shop') || n2.includes('garage') || n2.includes('shop')) {
    return 'exterior';
  }

  return 'interior';
};

/**
 * Convert template room data to floor plan format
 *
 * @param {Object} template - Template from floorPlanTemplates
 * @param {number} scaleFactor - Scale factor to apply (1 = original size)
 * @returns {Object} - Floor plan data with walls and rooms
 */
export const convertTemplateToFloorPlan = (template, scaleFactor = 1) => {
  const walls = [];
  const rooms = [];
  const wallMap = new Map(); // Track walls by position to avoid duplicates (key -> wallData)

  // Helper to create wall key for deduplication
  const getWallKey = (x1, y1, x2, y2) => {
    // Normalize the key so same wall in opposite direction has same key
    if (x1 > x2 || (x1 === x2 && y1 > y2)) {
      return `${x2},${y2}-${x1},${y1}`;
    }
    return `${x1},${y1}-${x2},${y2}`;
  };

  // Canvas offset to center the floor plan
  const offsetX = 200;
  const offsetY = 200;

  // Pre-calculate scaled dimensions for exterior wall detection
  const scaledTotalWidth = feetToPixels(template.totalWidth * scaleFactor);
  const scaledTotalHeight = feetToPixels(template.totalHeight * scaleFactor);

  // Helper to check if edge is on exterior boundary
  const isExteriorEdge = (edge) => {
    const isOnLeft = Math.abs(edge.x1 - offsetX) < 1 && Math.abs(edge.x2 - offsetX) < 1;
    const isOnRight = Math.abs(edge.x1 - (offsetX + scaledTotalWidth)) < 1 && Math.abs(edge.x2 - (offsetX + scaledTotalWidth)) < 1;
    const isOnTop = Math.abs(edge.y1 - offsetY) < 1 && Math.abs(edge.y2 - offsetY) < 1;
    const isOnBottom = Math.abs(edge.y1 - (offsetY + scaledTotalHeight)) < 1 && Math.abs(edge.y2 - (offsetY + scaledTotalHeight)) < 1;
    return isOnLeft || isOnRight || isOnTop || isOnBottom;
  };

  // Helper to get or create wall for an edge, returns the wall ID
  const getOrCreateWall = (edge, roomName) => {
    const wallKey = getWallKey(edge.x1, edge.y1, edge.x2, edge.y2);

    if (wallMap.has(wallKey)) {
      // Wall already exists, return its ID
      return wallMap.get(wallKey).id;
    }

    // Create new wall
    const isExterior = isExteriorEdge(edge);
    const wallData = {
      id: generateId(),
      start: { x: edge.x1, y: edge.y1 },
      end: { x: edge.x2, y: edge.y2 },
      type: getWallType(roomName, null, isExterior),
      height: 96, // 8 feet default
    };

    walls.push(wallData);
    wallMap.set(wallKey, wallData);
    return wallData.id;
  };

  // Process each room
  template.rooms.forEach((room) => {
    const x = feetToPixels(room.x * scaleFactor) + offsetX;
    const y = feetToPixels(room.y * scaleFactor) + offsetY;
    const width = feetToPixels(room.width * scaleFactor);
    const height = feetToPixels(room.height * scaleFactor);

    // Define the 4 edges for this room (clockwise from top)
    const edges = [
      { x1: x, y1: y, x2: x + width, y2: y },                    // Top
      { x1: x + width, y1: y, x2: x + width, y2: y + height },   // Right
      { x1: x + width, y1: y + height, x2: x, y2: y + height },  // Bottom
      { x1: x, y1: y + height, x2: x, y2: y },                   // Left
    ];

    // Get or create walls for each edge, collect their IDs
    const wallIds = edges.map(edge => getOrCreateWall(edge, room.name));

    // Create room with wallIds linking to its boundary walls
    const roomData = {
      id: generateId(),
      name: room.name,
      color: getRoomColor(room.name),
      wallIds, // Array of wall IDs that form this room's boundary
      points: [
        { x, y },
        { x: x + width, y },
        { x: x + width, y: y + height },
        { x, y: y + height },
      ],
    };
    rooms.push(roomData);
  });

  return {
    walls,
    rooms,
    doors: [],
    windows: [],
    furniture: [],
    stairs: [],
    electrical: [],
    plumbing: [],
    plumbingLines: [],
    dimensions: [],
    texts: [],
    lines: [],
    polylines: [],
    hatches: [],
    porches: [],
    roofs: [],
  };
};

/**
 * Create a complete floor from template
 */
export const createFloorFromTemplate = (template, scaleFactor = 1, floorName = 'Floor 1') => {
  const floorPlanData = convertTemplateToFloorPlan(template, scaleFactor);

  return {
    id: generateId(),
    name: floorName,
    ...floorPlanData,
  };
};
